import React, { useEffect, useState } from 'react';
import Loading from '../../misc/Loading';
import { useDispatch, useSelector } from 'react-redux';
import { validateAppToken, clearAppToken, setAppToken } from '../../../store/AppTokenAction';
import AppTokenGoogleServiceAccount from './appToken/AppTokenGoogleServiceAccount';
import AppTokenMicrosoftApplication from './appToken/AppTokenMicrosoftApplication';
import AppTokenMicrosoftDelegated from './appToken/AppTokenMicrosoftDelegated';
import { CardBody, CardTitle, Col, Button } from 'reactstrap';
import AppTokenModal from './AppTokenModal';
import { CoreAppToken, CoreAppTokenValidation } from '../../../models/CoreAppToken';
import { RequestState } from '../../../models/RequestState';
import StatusCode from '../../../util/StatusCode';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';
import { AppTokenType } from '../../../models/CoreModels';


interface AppTokenConfigurationProps {
    active: boolean,
    appToken: CoreAppToken,
    additionalScopes?: string[],
    disabled: boolean,
    invalid?: (invalid: boolean) => void
}


const AppTokenConfiguration = (props: AppTokenConfigurationProps) => {

    const [clearAppTokenModalOpen, setClearAppTokenModalOpen] = useState(false);

    const appTokenValidationRequestState = useSelector<RootState, RequestState<CoreAppTokenValidation>>(x => x.token.appTokenValidation);
    const appTokenRequestState = useSelector<RootState, RequestState<CoreAppToken>>(x => x.token.appToken);

    const dispatch = useDispatch();

    const { t } = useTranslation('configuration');

    useEffect(() => {
        setAppToken(props.appToken)(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (appTokenRequestState.code === StatusCode.COMPLETE) {
            revalidateAppToken(props.additionalScopes);
        }
    }, [appTokenRequestState, props.additionalScopes]);

    useEffect(() => {
        if (props.invalid) {
            const validation = validate();
            props.invalid(validation.length > 0);
        }
    }, [appTokenValidationRequestState, props.active])

    const revalidateAppToken = (additionalScopes?: string[]) => {
        validateAppToken(props.appToken.id, additionalScopes)(dispatch);
    }

    const validate = () => {
        const validation = [];

        if (appTokenValidationRequestState.code === StatusCode.COMPLETE && appTokenValidationRequestState.data.valid === false && props.active) {
            validation.push('inactiveAppToken');
        }

        return validation;
    };

    const removeAppToken = async () => {
        await clearAppToken(props.appToken.id)(dispatch)
        setClearAppTokenModalOpen(false);
    }

    const renderClearAppToken = () => {
        return (
            <AppTokenModal
                isOpen={clearAppTokenModalOpen}
                toggleModal={() => setClearAppTokenModalOpen(!clearAppTokenModalOpen)}
                header={t('appTokenConfiguration.removeHeader')}
                body={t('appTokenConfiguration.removeHeader')}
                confirm={removeAppToken}
                disabled={props.disabled}
            />
        )
    }

    const information = () => {

        if (appTokenValidationRequestState.code === StatusCode.ERROR) {
            return (
                <div>
                    <p>{t('appTokenConfiguration.validationError.failed')}</p>
                    <p>{t('appTokenConfiguration.validationError.response', { code: appTokenValidationRequestState.error.code })}</p>
                    <Button className="token_btn" onClick={() => revalidateAppToken()}>{t('appTokenConfiguration.validationError.retry')}</Button>
                </div>
            )
        }

        if (appTokenRequestState.code === StatusCode.ERROR) {
            return (
                <div>
                    <p>{t('appTokenConfiguration.validationError.failed')}</p>
                    <p>{t('appTokenConfiguration.validationError.response', { code: appTokenRequestState.error.code })}</p>
                    <Button className="token_btn" onClick={() => revalidateAppToken()}>{t('appTokenConfiguration.validationError.retry')}</Button>
                </div>
            )
        }

        if (appTokenRequestState.code === StatusCode.NONE || appTokenRequestState.code === StatusCode.PENDING) {
            return (<Loading />);
        }

        if (appTokenValidationRequestState.code === StatusCode.NONE || appTokenValidationRequestState.code === StatusCode.PENDING) {
            return (<Loading />);
        }

        const appToken = appTokenRequestState.data;
        let type = appToken.appTokenType;
        if (type === AppTokenType.GOOGLE_SERVICE_ACCOUNT) {
            return (
                <AppTokenGoogleServiceAccount
                    appToken={appToken}
                    disableInput={props.disabled}
                />
            )
        } else if (type === AppTokenType.MICROSOFT_APPLICATION) {
            return (
                <AppTokenMicrosoftApplication
                    appToken={appToken}
                    disableInput={props.disabled}
                />
            )
        } else if (type === AppTokenType.MICROSOFT_DELEGATED) {
            return (
                <AppTokenMicrosoftDelegated
                    appToken={appToken}
                    additionalScopes={props.additionalScopes}
                    disableInput={props.disabled}
                />
            )
        } else {
            return <div>{t('appTokenConfiguration.noSuchType')}</div>
        }
    }

    const validation = validate();
    return (
        <CardBody>
            <span style={{ display: 'flex' }}><CardTitle tag="h5">{t('appTokenConfiguration.cardTitle')}</CardTitle>
                {!props.disabled && <p className="p-xs p-link ml-2" onClick={() => setClearAppTokenModalOpen(true)}>{t('appTokenConfiguration.reset')}</p>}</span>
            {(props.invalid && validation.indexOf('inactiveAppToken') !== -1) &&
                <div className='push--bottom d-block invalid-feedback'>{t('appTokenConfiguration.invalidFeedback')}</div>
            }
            {information()}
            {clearAppTokenModalOpen && renderClearAppToken()}
        </CardBody>
    )
}

export default AppTokenConfiguration;
